import React from "react";
import { Smartphone, Download, X, Share, Plus, ChevronUp } from "lucide-react";

const DISMISS_KEY = "ema_pwa_dismissed_at";
const DISMISS_DAYS = 14;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIos = () => {
  const ua = window.navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !/(CriOS|FxiOS)/.test(ua); // Safari on iOS
};

const wasDismissedRecently = () => {
  const ts = localStorage.getItem(DISMISS_KEY);
  if (!ts) return false;
  const days = (Date.now() - Number(ts)) / 86400000;
  return days < DISMISS_DAYS;
};

export default function PwaInstallPrompt() {
  const [deferred, setDeferred] = React.useState(null);
  const [show, setShow] = React.useState(false);
  const [iosBanner, setIosBanner] = React.useState(false);

  React.useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS path: no beforeinstallprompt — show manual instructions after delay
    if (isIos()) {
      const t = setTimeout(() => setIosBanner(true), 1200);
      return () => { window.removeEventListener("beforeinstallprompt", onBefore); clearTimeout(t); };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null); setShow(false);
    if (outcome !== "accepted") localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const dismiss = () => {
    setShow(false); setIosBanner(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!show && !iosBanner) return null;

  return (
    <div className="fixed left-0 right-0 bottom-0 z-50 px-3 pb-3 pointer-events-none" data-testid="pwa-prompt">
      <div className="max-w-md mx-auto bg-white shadow-2xl border border-brand pointer-events-auto" data-testid="pwa-prompt-card">
        <div className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 bg-brand-dark text-white flex items-center justify-center flex-shrink-0">
            <Smartphone size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-brand-dark text-sm">Installér EMA Chauffør</div>
            {show && !iosBanner && (
              <p className="text-xs text-brand-text2 mt-1">Tilføj portalen til hjemmeskærmen — fullscreen, hurtigere, offline-venlig.</p>
            )}
            {iosBanner && (
              <p className="text-xs text-brand-text2 mt-1 leading-relaxed">
                Tryk <Share size={11} className="inline text-brand-red mx-0.5 -mt-0.5" /> i Safari og vælg <strong>"Føj til hjemmeskærm"</strong> <Plus size={11} className="inline text-brand-red mx-0.5 -mt-0.5" />.
              </p>
            )}
          </div>
          <button onClick={dismiss} className="text-brand-text2 hover:text-brand-dark p-1" aria-label="Luk" data-testid="pwa-dismiss">
            <X size={16} />
          </button>
        </div>
        {show && !iosBanner && (
          <div className="border-t border-brand p-3 flex gap-2">
            <button onClick={dismiss} data-testid="pwa-later" className="flex-1 btn-secondary py-2 px-3 text-xs justify-center">Senere</button>
            <button onClick={install} data-testid="pwa-install" className="flex-1 btn-primary py-2 px-3 text-xs justify-center">
              <Download size={14} /> Installér
            </button>
          </div>
        )}
        {iosBanner && (
          <div className="border-t border-brand p-3 text-[10px] uppercase tracking-widest text-brand-text2 flex items-center justify-center gap-2">
            <ChevronUp size={12} /> Find delings-ikonet i Safari-bjælken
          </div>
        )}
      </div>
    </div>
  );
}
